import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import debounce from "lodash/debounce";
import moment from "moment";
import mixpanel from "mixpanel-browser";
import ContentContainer from "../../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import { DataTable } from "../../../../ui-components/data-table";
import Tag from "../../../../ui-components/molecules/tag/tag";
import LabeledTextInput from "../../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import XGSSelect, { XGSSelectOption } from "../../../../ui-components/xgs-select/xgsSelect";
import { PAGE_IDS } from "../../../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../../../app/data/common/queryKeys";
import { Routes } from "../../../../app/route/RoutesConfig";
import InvoiceFactoringService from "../../../../app/data/invoice-factoring/invoiceFactoringService";
import {
  FactoredInvoiceListItem,
  FactoredInvoiceStatus,
  InvoiceListView,
} from "../../../../app/data/invoice-factoring/models";
import {
  FACTORING_FILTER_DEBOUNCE_MS,
  FACTORING_PAGE_SIZE,
  FACTORING_QUERY_OPTIONS,
  INVOICE_STATUS_COLORS,
  INVOICE_STATUS_LABELS,
  INVOICE_VIEW_LABELS,
  formatBillDate,
  formatCents,
  formatDateTime,
} from "../constants";
import { getInvoiceNextAction } from "../utils/nextAction";
import { useFactoringContext } from "../common/useFactoringContext";
import NextActionChip from "../common/nextActionChip";
import FactoringNav from "../common/factoringNav";
import "../invoiceFactoring.scss";

const invoiceFactoringService = InvoiceFactoringService.getInstance();

const columnHelper = createColumnHelper<FactoredInvoiceListItem>();

const statusOptions: XGSSelectOption[] = [
  { value: "", label: "All statuses" },
  ...(Object.keys(INVOICE_STATUS_LABELS) as FactoredInvoiceStatus[]).map((status) => ({
    value: status,
    label: INVOICE_STATUS_LABELS[status],
  })),
];

const viewOptions: XGSSelectOption[] = [
  { value: "", label: "All invoices" },
  { value: "needsAttention", label: `${INVOICE_VIEW_LABELS.needsAttention}: latest attempt failed or skipped` },
  { value: "documentGaps", label: `${INVOICE_VIEW_LABELS.documentGaps}: job created, documents not sent` },
];

const formatTrackedRange = (from: string, to: string) => {
  const start = from ? moment(from).format("MM/DD/YYYY") : "the start";
  const end = to ? moment(to).subtract(1, "millisecond").format("MM/DD/YYYY") : "now";
  return `First tracked ${start} to ${end}`;
};

const selectMenuStyles = {
  menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 1100 }),
};

const columns = [
  columnHelper.accessor("probillNumber", {
    id: "probillNumber",
    header: "Probill",
    size: 120,
    minSize: 100,
  }),
  columnHelper.accessor("status", {
    id: "status",
    header: "Status",
    cell: (info) => (
      <Tag mods={{ color: INVOICE_STATUS_COLORS[info.getValue()], uppercase: false }}>
        {INVOICE_STATUS_LABELS[info.getValue()]}
      </Tag>
    ),
    size: 160,
    minSize: 140,
  }),
  columnHelper.display({
    id: "nextAction",
    header: "Next action",
    cell: ({ row }) => <NextActionChip action={getInvoiceNextAction(row.original)} />,
    size: 260,
    minSize: 180,
  }),
  columnHelper.accessor("denimJobId", {
    id: "denimJobId",
    header: "Denim job",
    cell: (info) => (
      <div className="xgs-invoice-factoring__two-line">
        <span className="xgs-invoice-factoring__mono">{info.getValue() || "—"}</span>
        {info.row.original.denimJobStatus && (
          <span className="xgs-invoice-factoring__muted">{info.row.original.denimJobStatus}</span>
        )}
      </div>
    ),
    size: 170,
    minSize: 140,
  }),
  columnHelper.display({
    id: "documents",
    header: "Documents sent",
    cell: ({ row }) => `${row.original.documentsSentCount} of ${row.original.documentCount}`,
    size: 130,
    minSize: 110,
  }),
  columnHelper.accessor("payorName", {
    id: "payorName",
    header: "Payor",
    cell: (info) => info.getValue() || "—",
    size: 220,
    minSize: 160,
  }),
  columnHelper.accessor("invoiceAmountCents", {
    id: "invoiceAmount",
    header: "Amount",
    cell: (info) => formatCents(info.getValue()),
    size: 120,
    minSize: 100,
  }),
  columnHelper.accessor("billDate", {
    id: "billDate",
    header: "Bill date",
    cell: (info) => formatBillDate(info.getValue()),
    size: 110,
    minSize: 100,
  }),
  columnHelper.accessor("attemptCount", {
    id: "attemptCount",
    header: "Attempts",
    size: 90,
    minSize: 80,
  }),
  columnHelper.accessor("updatedAt", {
    id: "updatedAt",
    header: "Last update",
    cell: (info) => formatDateTime(info.getValue() || info.row.original.createdAt),
    size: 170,
    minSize: 150,
  }),
];

const FactoredInvoices: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { terminalPrefix } = useFactoringContext();

  const initialParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [searchInput, setSearchInput] = useState(() => initialParams.get("search") || "");
  const [search, setSearch] = useState(() => initialParams.get("search") || "");
  const [status, setStatus] = useState<FactoredInvoiceStatus | "">(
    () => (initialParams.get("status") as FactoredInvoiceStatus) || ""
  );
  const [view, setView] = useState<InvoiceListView | "">(() => {
    const value = initialParams.get("view") as InvoiceListView;
    return INVOICE_VIEW_LABELS[value] ? value : "";
  });
  const [tracked, setTracked] = useState(() => ({
    from: initialParams.get("trackedFrom") || "",
    to: initialParams.get("trackedTo") || "",
  }));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearch = useCallback(debounce(setSearch, FACTORING_FILTER_DEBOUNCE_MS), []);

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "invoice_factoring_invoices" });
  }, []);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (status) q.set("status", status);
    if (view) q.set("view", view);
    if (tracked.from) q.set("trackedFrom", tracked.from);
    if (tracked.to) q.set("trackedTo", tracked.to);
    const qs = q.toString();
    history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, view, tracked]);

  const { data, fetchNextPage, isFetching, isFetchingNextPage, isLoading, isPreviousData, isError, error } =
    useInfiniteQuery({
      queryKey: [QUERY_KEYS.INVOICE_FACTORING_INVOICES, search, status, view, tracked.from, tracked.to],
      queryFn: ({ pageParam = 1 }) =>
        invoiceFactoringService.getInvoices({
          page: pageParam,
          pageSize: FACTORING_PAGE_SIZE,
          search: search || undefined,
          status: status || undefined,
          view: view || undefined,
          trackedFrom: tracked.from || undefined,
          trackedTo: tracked.to || undefined,
        }),
      getNextPageParam: (lastPage) => lastPage.pagination?.next ?? undefined,
      keepPreviousData: true,
      retry: false,
      ...FACTORING_QUERY_OPTIONS,
    });

  const invoices = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const hasNextPage = !!data?.pages[data.pages.length - 1]?.pagination?.next;
  const total = data?.pages[0]?.pagination?.total;

  const onClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatus("");
    setView("");
    setTracked({ from: "", to: "" });
  };

  const hasTrackedRange = !!(tracked.from || tracked.to);

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ "full-width": true }}
      pageId={PAGE_IDS.INVOICE_FACTORING_INVOICES}
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.INVOICE_FACTORING_INVOICES}
          title="Factored Invoices"
          mods={{ "full-width": true }}
        />
      }
    >
      <div className="xgs-invoice-factoring">
        <FactoringNav />

        <div className="xgs-invoice-factoring__filters">
          <LabeledTextInput
            label="Search:"
            labelMode={LabelModes.column}
            type="text"
            value={searchInput}
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value;
              setSearchInput(value);
              debouncedSetSearch(value);
            }}
            placeholder="Probill, Denim job, BOL, or payor"
            className="xgs-invoice-factoring__filter-field xgs-invoice-factoring__filter-search"
          />
          <div className="xgs-invoice-factoring__filter-field xgs-invoice-factoring__filter-select">
            <span className="xgs-invoice-factoring__filter-label">Status:</span>
            <XGSSelect
              value={statusOptions.find((o) => o.value === status) ?? statusOptions[0]}
              options={statusOptions}
              onValueChange={(o) => setStatus((o?.value as FactoredInvoiceStatus) || "")}
              isClearable={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuStyles}
            />
          </div>
          <div className="xgs-invoice-factoring__filter-field xgs-invoice-factoring__filter-select xgs-invoice-factoring__filter-select--wide">
            <span className="xgs-invoice-factoring__filter-label">Show:</span>
            <XGSSelect
              value={viewOptions.find((o) => o.value === view) ?? viewOptions[0]}
              options={viewOptions}
              onValueChange={(o) => setView((o?.value as InvoiceListView) || "")}
              isClearable={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuStyles}
            />
          </div>
          {hasTrackedRange && (
            <span className="xgs-invoice-factoring__muted">{formatTrackedRange(tracked.from, tracked.to)}</span>
          )}
          {(search || status || view || hasTrackedRange) && (
            <Button theme={ButtonThemes.blueInverted} type="button" onClick={onClearFilters} className="xgs-invoice-factoring__filter-clear">
              Clear filters
            </Button>
          )}
          {total !== undefined && <span className="xgs-invoice-factoring__muted">{total} invoices</span>}
        </div>

        {isError && (
          <div className="xgs-invoice-factoring__error-box">Unable to load invoices. {(error as Error)?.message}</div>
        )}

        <DataTable
          columns={columns}
          data={invoices}
          isLoading={isLoading || (isFetching && isPreviousData)}
          keepTableOnLoading
          cursorPointer
          rowHeight={52}
          noResultsText="No factored invoices found. Invoices appear here once they are included in an upload batch."
          infiniteScroll
          infiniteScrollLoading={isFetchingNextPage}
          infiniteScrollHasNext={hasNextPage}
          onInfiniteScroll={() => fetchNextPage()}
          onRowClicked={(row: FactoredInvoiceListItem) =>
            history.push(`${terminalPrefix}${Routes.billing.invoiceFactoringInvoices}/${row.probillNumber}`)
          }
          stickyHeaders
          responsive
        />
      </div>
    </ContentContainer>
  );
};

export default FactoredInvoices;
